import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { History } from 'lucide-react';
import { cpApi, type CpProfileFieldAuditItem } from '../api/api';

const FIELD_KEYS: Record<string, string> = {
  name: 'qrForm.auditFieldName',
  bank_name: 'qrForm.auditFieldBank',
  bank_account_no: 'qrForm.accountNo',
  primary_account_no: 'qrForm.primaryAccount',
  qr_account_mode: 'qrForm.qrAccountMode',
  account_holder_name: 'qrForm.accountHolderName',
  national_id: 'qrForm.nationalId',
  phone_number: 'qrForm.phoneNumber',
  work_mode: 'qrForm.workMode',
  payment_provider: 'qrForm.paymentProvider',
  is_active: 'qrForm.profileActive',
  is_archived: 'qrForm.archive',
  is_deleted: 'qrForm.archive',
  is_confirmed: 'qrProfiles.confirmed',
  daily_limit: 'qrForm.dailyVnd',
  monthly_limit: 'qrForm.monthlyVnd',
  tick_max_requests: 'qrForm.tickMax',
  tick_window_minutes: 'qrForm.tickWindow',
  min_amount: 'qrForm.minVnd',
  max_amount: 'qrForm.maxVnd',
  max_balance_vnd: 'qrForm.maxBalance',
  max_requests_per_day: 'qrForm.maxPerDay',
  rule_priority: 'qrForm.rulePriority',
  rule_comment: 'qrForm.ruleComment',
  sms_enabled: 'qrForm.smsTitle',
};

function formatValue(field: string, raw?: string | null, t?: (k: string) => string) {
  if (raw == null || raw === '') return '—';
  const v = raw.trim();
  if (v === 'true' || v === 't') return t ? t('qrForm.auditOn') : 'on';
  if (v === 'false' || v === 'f') return t ? t('qrForm.auditOff') : 'off';
  if (field === 'qr_account_mode') {
    if (v === 'primary') return t ? t('qrForm.qrModePrimary') : v;
    if (v === 'virtual') return t ? t('qrForm.qrModeVirtual') : v;
  }
  return v;
}

export default function VndQrProfileAuditPanel({ profileId }: { profileId: number | null }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<CpProfileFieldAuditItem[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const load = useCallback(async () => {
    if (!profileId) return;
    try {
      const res = await cpApi.getVndQrProfileAudit(profileId, { page, limit });
      setItems(res.items || []);
      setTotal(Number(res.total ?? 0));
      setTotalPages(Math.max(1, Number(res.total_pages ?? 1)));
      setErr(null);
    } catch {
      setErr(t('qrForm.auditLoadError'));
    }
  }, [profileId, page, t]);

  useEffect(() => {
    setPage(1);
  }, [profileId]);

  useEffect(() => {
    void load();
    if (!profileId) return;
    const timer = window.setInterval(() => void load(), 15000);
    return () => window.clearInterval(timer);
  }, [load, profileId]);

  if (!profileId) return null;

  return (
    <section className="rounded-xl border border-slate-200 bg-white px-3.5 py-3 dark:border-white/10 dark:bg-white/[0.04]">
      <button type="button" onClick={() => setOpen((v) => !v)} className="mb-2 flex w-full items-start gap-2.5 text-left">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-white shadow-sm">
          <History className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <div className="text-sm font-medium text-slate-800 dark:text-slate-100">{t('qrForm.auditTitle')}</div>
          <p className="text-[11px] leading-snug text-slate-500 dark:text-slate-400">{t('qrForm.auditHint')}</p>
        </div>
        <span className="pt-1 text-[11px] text-slate-400">{open ? t('qrForm.auditCollapse') : t('qrForm.auditExpand')}</span>
      </button>
      {open ? (
        err ? (
          <p className="text-[12px] text-rose-700 dark:text-rose-300">{err}</p>
        ) : items.length === 0 ? (
          <p className="text-[12px] text-slate-500 dark:text-slate-400">{t('qrForm.auditEmpty')}</p>
        ) : (
          <>
            <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-100 dark:border-white/10">
              <table className="w-full text-left text-[11px]">
                <thead className="sticky top-0 bg-slate-50 text-slate-500 dark:bg-white/[0.06] dark:text-slate-400">
                  <tr>
                    <th className="px-2 py-1.5 font-medium">{t('qrForm.auditWhen')}</th>
                    <th className="px-2 py-1.5 font-medium">{t('qrForm.auditWho')}</th>
                    <th className="px-2 py-1.5 font-medium">{t('qrForm.auditWhat')}</th>
                    <th className="px-2 py-1.5 font-medium">{t('qrForm.auditOld')}</th>
                    <th className="px-2 py-1.5 font-medium">{t('qrForm.auditNew')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.id} className="border-t border-slate-100 text-slate-700 dark:border-white/10 dark:text-slate-200">
                      <td className="whitespace-nowrap px-2 py-1.5 text-slate-500 dark:text-slate-400">
                        {it.created_at
                          ? new Date(it.created_at).toLocaleString(i18n.language, {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </td>
                      <td className="px-2 py-1.5">{(it.actor || t('qrForm.auditSystem')).trim()}</td>
                      <td className="px-2 py-1.5 font-medium text-slate-800 dark:text-slate-100">
                        {FIELD_KEYS[it.field] ? t(FIELD_KEYS[it.field]) : it.field}
                      </td>
                      <td className="max-w-[9rem] truncate px-2 py-1.5 text-slate-500" title={it.old_value || ''}>
                        {formatValue(it.field, it.old_value, t)}
                      </td>
                      <td className="max-w-[9rem] truncate px-2 py-1.5" title={it.new_value || ''}>
                        {formatValue(it.field, it.new_value, t)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-500">
              <span>{t('qrForm.auditCount', { total, page, totalPages })}</span>
              <div className="flex gap-1.5">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700 disabled:opacity-40 dark:border-white/15 dark:bg-white/5 dark:text-slate-200"
                >
                  {t('qrForm.prevPage')}
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-700 disabled:opacity-40 dark:border-white/15 dark:bg-white/5 dark:text-slate-200"
                >
                  {t('qrForm.nextPage')}
                </button>
              </div>
            </div>
          </>
        )
      ) : null}
    </section>
  );
}
